/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { Button } from '@mpb-com/feather/components/atoms/button';
import {
    Choice,
    ChoiceLabelSize,
    ChoiceVariant,
} from '@mpb-com/feather/components/atoms/choice';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { IconSize } from '@mpb-com/feather/components/atoms/icon';
import { Notification } from '@mpb-com/feather/components/atoms/notification';
import { NotificationVariant } from '@mpb-com/feather/components/atoms/notification/types';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { TFunction } from 'i18next';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';
import { object } from 'yup';

import { FormValues } from '@toucan/services/request-client';

import {
    email,
    firstName,
    lastName,
    password,
} from '@toucan/helpers/validationRules';

import { PasswordInput } from '@toucan/client/blocks/components/password-input';
import { MarketingUnsubscribeDisclaimer } from '@toucan/client/features/customer/components/marketing-unsubscribe-disclaimer';
import { Input } from '@toucan/client/shared/components/input';
import { Link } from '@toucan/client/shared/components/link';

import { styles } from './sign-up-form.styles';

export type SignUpFormProps = {
    /**
     * Initial values for the form fields.
     *
     * @default See usage examples
     */
    initialValues?: FormValues;

    /**
     * Initial error message to display
     */
    initialStatus?: ReactNode;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Submit form handler
     */
    onSignUp: (
        values: FormValues,
        formActions: FormikHelpers<FormValues>
    ) => void;

    /**
     * Handle opening the login modal
     *
     * @param event {MouseEvent<HTMLElement>} The mouse event
     */
    onOpenLogin?: (event: React.MouseEvent<HTMLElement>) => void;
};

export type SignUpFormStyleProps = Omit<SignUpFormProps, 'onSignUp'>;

const StyledForm = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})<SignUpFormStyleProps>`
    ${styles}
`;

export const getSignupValidationSchema = (t: TFunction) =>
    object().shape({
        firstName: firstName(t),
        lastName: lastName(t),
        emailAddress: email(t),
        password: password(t),
    });

export const SignUpForm: FunctionComponent<SignUpFormProps> = ({
    initialValues = {
        firstName: '',
        lastName: '',
        emailAddress: '',
        password: '',
        confirmPassword: '',
        receiveOffersViaEmail: false,
    },
    initialStatus,
    onSignUp,
    onOpenLogin,
    ...otherProps
}: SignUpFormProps) => {
    const { t } = useTranslation();
    const validationSchema = getSignupValidationSchema(t);

    return (
        <StyledForm {...otherProps}>
            <Heading
                size={HeadingSize.H1}
                className="create-account-heading text-center"
            >
                {t('Create an account')}
            </Heading>
            <p className="text-center">
                {t('Already have an account? ')}
                <Link
                    data-testid="sign-up-form__log-in-button"
                    onClick={onOpenLogin}
                >
                    {t('Log in')}
                </Link>
            </p>
            <Formik
                initialValues={initialValues}
                onSubmit={onSignUp}
                validationSchema={validationSchema}
                initialStatus={initialStatus}
            >
                {({ errors, touched, status, isSubmitting }) => (
                    <Form className="grid-container" data-testid="sign-up-form">
                        <Field name="firstName">
                            {({ field }: FieldProps) => (
                                <Input
                                    {...field}
                                    type="text"
                                    label={t('First name')}
                                    error={errors[field.name]}
                                    touched={touched[field.name]}
                                    className="first-name"
                                    required={true}
                                />
                            )}
                        </Field>
                        <Field name="lastName">
                            {({ field }: FieldProps) => (
                                <Input
                                    {...field}
                                    type="text"
                                    label={t('Last name')}
                                    error={errors[field.name]}
                                    touched={touched[field.name]}
                                    className="last-name"
                                    required={true}
                                />
                            )}
                        </Field>
                        <Field name="emailAddress">
                            {({ field }: FieldProps) => (
                                <Input
                                    {...field}
                                    type="text"
                                    label={t('Email address')}
                                    error={errors[field.name]}
                                    touched={touched[field.name]}
                                    className="email-address"
                                    required={true}
                                />
                            )}
                        </Field>
                        <Field name="password">
                            {({ field }: FieldProps) => (
                                <PasswordInput
                                    {...field}
                                    label={t('Password')}
                                    showLabel={t('Show')}
                                    hideLabel={t('Hide')}
                                    error={errors[field.name]}
                                    touched={touched[field.name]}
                                    className="password"
                                    required={true}
                                    helpText={t('At least 8 characters')}
                                />
                            )}
                        </Field>
                        <Field name="receiveOffersViaEmail">
                            {({ field }: FieldProps) => (
                                <div className="receive-email-marketing">
                                    <Choice
                                        {...field}
                                        label={t(
                                            'Yes, please send me offers and original content by email.'
                                        )}
                                        value="true"
                                        variant={ChoiceVariant.CHECKBOX}
                                        checked={field.value}
                                        labelSize={ChoiceLabelSize.SMALL}
                                    />
                                </div>
                            )}
                        </Field>
                        <div className="submit">
                            {status && (
                                <Notification
                                    variant={NotificationVariant.ERROR}
                                    data-testid="sign-up-form__error-message"
                                    className="error-message"
                                    iconSize={IconSize.L}
                                >
                                    {status}
                                </Notification>
                            )}
                            <Button type="submit" disabled={isSubmitting}>
                                {t('Join MPB')}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
            <MarketingUnsubscribeDisclaimer className="notice" />
        </StyledForm>
    );
};
